import { Flex, Tooltip, Typography } from 'antd';
import React from 'react';
import { KPI } from 'screens/season-overview/types';
import { takeActionBasedOnCurrentProgressKPI } from 'screens/season-overview/util';
import { formatCurrencyShortened, renderInfoValue } from 'utils/helpers';

const { Text } = Typography;

type KPIRendererProps = {
  data: KPI;
  percentageLowerLimits?: {
    onTrack: number;
    offTrack: number;
  };
  title?: string;
  renderValue?: (achieved: number, target: number) => React.ReactNode;
  currency?: string;
  numeratorKey?: keyof KPI;
  denominatorKey?: keyof KPI;
};

const KPIRenderer: React.FC<KPIRendererProps> = ({
  data,
  renderValue,
  percentageLowerLimits,
  currency = '',
  denominatorKey = 'totalTarget',
}) => {
  const { actualTarget, totalTarget, actualTargetPercent } = data || {};

  // Determine color based on percentage
  const getColorAndIcon = (): { color: string } => {
    return takeActionBasedOnCurrentProgressKPI(data, {
      onTrack: {
        lowerLimitPercentage: percentageLowerLimits?.onTrack || 90,
        action: () => ({ color: '#389E0D' }),
      },
      offTrack: {
        lowerLimitPercentage: percentageLowerLimits?.offTrack || 80,
        action: () => ({ color: '#F90' }),
      },
      critical: {
        action: () => ({ color: '#F5222D' }),
      },
    });
  };

  const { color } = getColorAndIcon();

  const defaultValueRenderer = (achieved: number, target: number) => {
    achieved = renderInfoValue(achieved, '0') as number;
    target = renderInfoValue(target, '0') as number;

    if (currency) {
      return `${currency} ${formatCurrencyShortened(achieved)}/${currency} ${formatCurrencyShortened(
        target,
      )}`;
    }
    return `${achieved?.toLocaleString()}/${target?.toLocaleString()}`;
  };

  const denominator = data?.[denominatorKey!] || totalTarget;

  const valueDisplay = renderValue
    ? renderValue(actualTarget, denominator)
    : defaultValueRenderer(actualTarget, denominator);

  const isNotStarted = !actualTarget && !totalTarget;

  return (
    <Flex vertical style={{ fontFamily: 'Poppins' }}>
      <Tooltip
        title={isNotStarted ? 'Not started' : undefined}
        placement="topLeft"
        className="w-min"
      >
        <Text
          className="season-overview__widget-text--title"
          style={{
            color: isNotStarted ? '#c1b9b9' : color,
            width: 'fit-content',
          }}
        >
          {(actualTargetPercent || 0)?.toFixed()}%
        </Text>
      </Tooltip>

      <Text type="secondary" className="season-overview__widget-text--helper">
        {valueDisplay}
      </Text>
    </Flex>
  );
};

export default KPIRenderer;
